import React, { PropTypes, Component } from 'react'
import { connect } from 'react-redux'
import { reduxForm, Field } from 'redux-form'
import { createSelector, createStructuredSelector } from 'reselect'
import moment from 'moment'

import { load, schemas } from 'api'
import { currentUserAddressSelector, currentUserSelector } from 'auth'
import { Button, Heading } from 'core'
import { FormContainer, FormSection, FormRule } from 'form'
import { applyHOCs, selectSlug, formatDateRange } from 'helpers'
import { Spacer } from 'layout'
import { OrderSummaryTable, OrderSummaryPropTypes, DiscountCodeForm } from 'order-summary'
import { page, pageLoadSelectors, PageHead } from 'page'
import { PaymentForm, withStripeElements } from 'stripe'

import { INDIVIDUAL_RESERVATION_FORM_NAME as FORM_NAME } from '../constants'
import { reserve, loadIntakeQuestions } from './actions'
import {
  formIsCompleteSelector,
  getIndividualReservationOrderSummary,
  invoiceRequestedSelector
} from './selectors'
import { InvoiceRequestForm } from 'invoice-request-form'
import InvoiceRequestSelector from './InvoiceRequestSelector'
import LoginOrCreateAccountForm from './LoginOrCreateAccountForm'
import IntakeQuestionsForm from './IntakeQuestionsForm'

export class IndividualReservationPage extends Component {
  componentDidMount () {
    this.loadIntakeQuestionsIfNeeded()
  }

  componentDidUpdate (prevProps) {
    if (prevProps.sessionSale?.id !== this.props.sessionSale?.id) {
      this.loadIntakeQuestionsIfNeeded()
    }
  }

  loadIntakeQuestionsIfNeeded = () => {
    const sessionSale = (
      this.props?.isInstitutionRegistration &&
      this.props.sessionSaleWithInstitutionPreview
    ) || this.props.sessionSale

    if (sessionSale?.id && this.props.loadIntakeQuestions) {
      this.props.loadIntakeQuestions(sessionSale.id)
    }
  }

  render () {
    const {
      submitting,
      formIsComplete,
      reserve,
      stripeElements,
      orderSummary,
      invoiceRequested,
      currentUser,
      intakeQuestions
    } = this.props

    const sessionSale = (
      this.props?.isInstitutionRegistration &&
      this.props.sessionSaleWithInstitutionPreview
    ) || this.props.sessionSale

    const isInvoiceEnabled = sessionSale.enable_invoice_request

    return (
      <div>
        <PageHead title={`Register for ${sessionSale.title}`} />
        <Heading size='h6' freight center noMargin>
          {sessionSale.title}
        </Heading>
        {sessionSale.advertised_start_date && sessionSale.advertised_end_date && (
          <Heading size='h6' freight center noMargin>
            {formatDateRange(sessionSale.advertised_start_date, sessionSale.advertised_end_date)}
          </Heading>
        )}
        <FormContainer name={`SESSION REGISTER - ${sessionSale.slug}`} onSubmit={reserve}>
          {isInvoiceEnabled ? <Field name='request_invoice' component={InvoiceRequestSelector} /> : ''}
          <FormSection>
            <Heading size='h6'>Order Summary</Heading>
            {currentUser
              ? <Heading size='h6' weight='medium'>Registration For: {currentUser.name}</Heading>
              : <LoginOrCreateAccountForm form={FORM_NAME} />}
            <FormRule />
            <OrderSummaryTable {...orderSummary} />
            <Spacer size='xs' />
            <DiscountCodeForm
              parentForm={FORM_NAME}
              productId={sessionSale.reservation_product_id}
            />
            {intakeQuestions?.length > 0 && (
              <div>
                <FormRule />
                <IntakeQuestionsForm intakeQuestions={intakeQuestions || []} />
              </div>
            )}
            {orderSummary.total > 0 && [
              <FormRule key={0} />,
              invoiceRequested
                ? <InvoiceRequestForm key='invoice-form' form={FORM_NAME} />
                : <PaymentForm key='payment-form' form={FORM_NAME} stripeElements={stripeElements} />
            ]}
          </FormSection>
          <FormSection>
            <Button
              type='submit'
              size='primary'
              noMargin
              disabled={(orderSummary.total > 0 && !formIsComplete) || submitting}
              isLoading={submitting}
            >
              {invoiceRequested ? 'Send Invoice' : 'Submit Payment'}
            </Button>
          </FormSection>
        </FormContainer>
      </div>
    )
  }
}

IndividualReservationPage.propTypes = {
  sessionSale: PropTypes.shape({
    title: PropTypes.string.isRequired,
    reservation_price: PropTypes.number.isRequired,
    reservation_product_id: PropTypes.number.isRequired,
    isInvoiceEnabled: PropTypes.bool
  }).isRequired,
  reserve: PropTypes.func.isRequired,
  stripeElements: PropTypes.object.isRequired,
  formIsComplete: PropTypes.bool,
  orderSummary: PropTypes.shape(OrderSummaryPropTypes).isRequired,
  currentUser: PropTypes.object
}

export const reservationsAreAvailable = sessionSale => {
  const openRegistrationClosed = moment().isAfter(sessionSale.open_registration_end_at)

  if (openRegistrationClosed) {
    return false
  } else if (sessionSale.registration_status === 'reserved') {
    return false
  } else if (sessionSale.num_reservations_available === 0) {
    return false
  } else {
    return true
  }
}

export default applyHOCs([
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true
  }),
  connect(
    createStructuredSelector({
      initialValues: createSelector(
        currentUserAddressSelector,
        currentUserAddress => ({ ...currentUserAddress, request_invoice: false })
      ),
      isInstitutionRegistration: state => state?.individualReservations?.selectedInstitution,
      orderSummary: getIndividualReservationOrderSummary,
      invoiceRequested: invoiceRequestedSelector,
      formIsComplete: formIsCompleteSelector,
      currentUser: currentUserSelector,
      intakeQuestions: state => state.individualReservations?.intakeQuestions
    }),
    (dispatch, props) => ({
      reserve: () => dispatch(reserve(props)),
      loadIntakeQuestions: (sessionSaleId) => dispatch(loadIntakeQuestions(sessionSaleId))
    })
  ),
  withStripeElements(),
  page({
    layout: 'form',
    ...pageLoadSelectors('sessionSale'),
    redirectSelector: (state, { sessionSale }) => {
      if (sessionSale && !reservationsAreAvailable(sessionSale)) {
        return `/sessions/${sessionSale.slug}`
      }
    },
    noPageHead: true
  }),
  load({
    // kept separate from sessionSale below since updating URL causes entire page to render null and remount component state
    sessionSaleWithInstitutionPreview: {
      urlSelector: (state, props) => {
        const registrationInstitutionId = state?.individualReservations?.selectedInstitution?.id
        return registrationInstitutionId
          ? `/v2/session_sales/slug/${props.slug}?price_preview_institution_id=${registrationInstitutionId}`
          : `/v2/session_sales/slug/${props.slug}`
      },
      schema: schemas.sessionSale
    },
    sessionSale: {
      urlSelector: (state, props) => {
        return `/v2/session_sales/slug/${props.slug}`
      },
      schema: schemas.sessionSale
    }
  }),
  selectSlug()
])(IndividualReservationPage)
